import { IComponentOptions, IOnChangesObjectOf, IPromise, IQService, ITimeoutService } from "angular";
import { AssigneeActions, IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IGoal, IGoalParent } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { ICollection } from "@gtmhub/models";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import {
  IParentOkrSelectorEntry,
  IParentOkrSelectorSearchResult,
  IParentOkrSelectorSuggestion,
  IParentOkrSelectorSuggestionSession,
} from "@gtmhub/shared/components/parent-okr-selector/models";
import { blurFocusedElement } from "@gtmhub/shared/dom";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { ISearchCondition, ISearchRequestsBody, SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";

const AVATAR_PREVIEW_NUMBER = 5;
const SUGGESTION_TAKE_ENTRIES_NUMBER = 10;

export interface ParentOkrSelectorCtrlBindings {
  selectedOkr: Partial<IParentOkrSelectorEntry>;
  sessionIds: string[];
  ownerIds: string[];
  excludeId: string;
  placeholder: string;
  disabled: boolean;
  borderless: boolean;

  onChange(args: { parent: IGoalParent }): void;
}

interface IParentOkrSelectorRedux {
  assigneesMap: IAssigneeIdMap;
  teams: ITeam[];
  reduxLoadingIndicators: {
    ownerIdsTeamsAndManagerIds: IIndicator;
  };
}

const bindStateToCtrl = (state: ITeamsStoreState & IAssigneesStoreState): IParentOkrSelectorRedux => {
  const { items: teams, isFetched } = state.teams;

  const ownerIdsTeamsAndManagerIds: IIndicator = {
    progress: !isFetched,
  };

  return {
    assigneesMap: state.assignees.map,
    teams,
    reduxLoadingIndicators: {
      ownerIdsTeamsAndManagerIds,
    },
  };
};

export class ParentOkrSelectorCtrl extends GtmhubController implements ParentOkrSelectorCtrlBindings, IParentOkrSelectorRedux {
  // Bindings
  public selectedOkr: Partial<IParentOkrSelectorEntry>;
  public sessionIds: string[];
  public ownerIds: string[];
  public excludeId: string;
  public placeholder: string;
  public disabled: boolean;
  public borderless: boolean;

  public onChange: (args: { parent: IGoalParent }) => void;

  public searchTerm: string;
  public searchResults: IParentOkrSelectorSearchResult[];
  public suggestions: IParentOkrSelectorSuggestionSession[];
  public selected: IParentOkrSelectorEntry[];
  public loadingIndicators: {
    selectedItem: IIndicator;
    search: IIndicator;
  };
  public reduxLoadingIndicators: {
    ownerIdsTeamsAndManagerIds: IIndicator;
  };
  public focused: boolean;
  public avatarPreviewNumber = AVATAR_PREVIEW_NUMBER;
  public teams: ITeam[];
  public assigneesMap: IAssigneeIdMap;

  private previouslySelected: IParentOkrSelectorEntry[];
  private ownerIdsToFilterBy: string[];

  public static $inject = [
    "$timeout",
    "$q",
    "$ngRedux",
    "SearchService",
    "GoalService",
    "MetricService",
    "UIErrorHandlingService",
    "AnalyticsService",
    "TeamsActions",
    "AssigneeActions",
  ];

  constructor(
    private $timeout: ITimeoutService,
    private $q: IQService,
    private $ngRedux: INgRedux,
    private searchService: SearchService,
    private goalService: GoalService,
    private metricService: MetricService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private analyticsService: AnalyticsService,
    private teamsActions: TeamsActions,
    private assigneeActions: AssigneeActions
  ) {
    super();

    this.loadingIndicators = {
      selectedItem: {
        progress: false,
      },
      search: {
        progress: false,
      },
    };

    this.selected = [];
    this.previouslySelected = [];
    this.searchResults = null;
    this.suggestions = null;
    this.ownerIdsToFilterBy = [];

    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this));
  }

  $onInit(): void {
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
  }

  $onChanges(changesObj: IOnChangesObjectOf<ParentOkrSelectorCtrlBindings>): void {
    if (changesObj.selectedOkr?.currentValue) {
      this.loadParent(changesObj.selectedOkr.currentValue);
    }
  }

  public reset(): void {
    if (this.selected.length > 0) {
      this.previouslySelected = this.selected;
      this.selected = [];
    }
    this.searchResults = null;
    this.suggestions = null;
  }

  public handleSelectTag(selectedSearchResult?: IParentOkrSelectorSearchResult): boolean {
    if (selectedSearchResult) {
      const selectedIndex = this.searchResults.findIndex((it) => it.id === selectedSearchResult.id);

      if (selectedIndex > -1) {
        this.analyticsService.track("Parent OKR Search Selected", { selected_index: selectedIndex });
      }
    } else {
      this.analyticsService.track("Parent OKR Suggestion Selected");
    }

    // Since the ng-tags-input library triggers a focus with a timeout on tag-added event,
    // There is a need of delay so the force blur below to be executed after the focus
    this.$timeout(() => {
      blurFocusedElement();
    }, 50);

    return true;
  }

  public clearPreviouslySelected(): boolean {
    this.previouslySelected = [];

    return true;
  }

  public search = (searchTerm: string): Promise<IParentOkrSelectorSearchResult[]> => {
    if (!this.focused) {
      return Promise.resolve([]);
    }

    this.searchTerm = searchTerm;
    this.loadingIndicators.search = {
      progress: true,
    };

    this.getUsersTeamsAndManagers(this.ownerIds);

    if (searchTerm === "") {
      return this.loadSuggestions()
        .then(() => [])
        .finally(this.afterSearch);
    }

    return this.searchByTerm(searchTerm).finally(this.afterSearch);
  };

  public onFocus(): void {
    this.focused = true;
    this.reset();
  }

  public onBlur(): void {
    if (this.selected.length === 0) {
      this.selected = this.previouslySelected;
      this.previouslySelected = [];
    }

    const [parent] = this.selected;

    if (this.onChange) {
      this.onChange({ parent });
    }

    this.focused = false;
    this.suggestions = [];
  }

  public getPlaceholder(): string {
    if (this.selected.length > 0) {
      // Needs to be space, because otherwise it defaults to "Add a tag"
      return " ";
    }

    let key: string;

    if (this.focused) {
      key = "search_by_okr_or_owner";
    } else {
      key = this.placeholder || "select_parent_objective_or_kr";
    }

    return localize(key);
  }

  private loadSuggestions = (): IPromise<void> => {
    let goals: ICollection<Search<"goals">>;

    return this.loadSuggestionGoals()
      .then((res) => {
        goals = res;

        const goalIds = goals.items.map((it) => it.id);
        return this.loadSuggestionMetricsForGoals(goalIds);
      })
      .then((metrics) => {
        const allSuggestions = goals.items.map((item) => {
          const goalMetrics = metrics.items.filter((m) => m.fields.goalId === item.id) || [];
          const subItems = goalMetrics.map((it) => Object.assign({}, it.fields, { id: it.id, type: "metric" }));

          return Object.assign({}, item.fields, {
            id: item.id,
            type: "goal",
            subItems,
            expanded: false,
          }) as unknown as IParentOkrSelectorSuggestion;
        });

        const allSessionsIds = goals.items.map((it) => it.fields.sessionId);
        const uniqueSessionIds = [...new Set(allSessionsIds)];

        const sessionIds = this.sessionIds?.filter((it) => uniqueSessionIds.includes(it)) || uniqueSessionIds;

        this.searchResults = [];

        this.suggestions = sessionIds.map((sessionId) => {
          const goals = allSuggestions.filter((it) => it.sessionId === sessionId).sort(this.sortByOwnerIdOccurrencesAndIdOrder);

          const firstGoalWithMetrics = goals.find((it) => it.subItems.length > 0);

          if (firstGoalWithMetrics) {
            firstGoalWithMetrics.expanded = true;
          }

          return {
            sessionTitle: goals[0]?.session?.title,
            goals,
          };
        });
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  };

  private loadSuggestionGoals = (): IPromise<ICollection<Search<"goals">>> => {
    const sessions = this.sessionIds?.filter(Boolean);
    const searchConditions: ISearchCondition[] = [];

    if (sessions?.length > 0) {
      searchConditions.push({
        fieldName: "sessionId",
        fieldCondition: {
          operator: "in",
          value: sessions,
        },
      });
    }

    if (this.ownerIdsToFilterBy?.length > 0) {
      searchConditions.push({
        fieldName: "ownerIds",
        fieldCondition: {
          operator: "in",
          value: this.ownerIdsToFilterBy,
        },
      });
    }

    if (this.excludeId) {
      searchConditions.push({
        fieldName: "_id",
        fieldCondition: {
          operator: "isNot",
          value: this.excludeId,
        },
      });
    }

    const searchRequests: ISearchRequestsBody[] = [
      {
        collectionName: "goals",
        searchConditions,
      },
    ];

    const searchBody: SearchDTO = {
      searchRequests,
    };

    return this.searchService.getSomeSearchData<Search<"goals">>(searchBody, {
      take: SUGGESTION_TAKE_ENTRIES_NUMBER,
      skip: 0,
    });
  };

  private loadSuggestionMetricsForGoals = (goalIds: string[]): IPromise<ICollection<Search<"metrics">>> => {
    if (!goalIds || goalIds.length === 0) {
      return;
    }
    const searchConditions: ISearchCondition[] = [
      {
        fieldName: "goalId",
        fieldCondition: {
          operator: "in",
          value: goalIds,
        },
      },
    ];

    if (this.excludeId) {
      searchConditions.push({
        fieldName: "_id",
        fieldCondition: {
          operator: "isNot",
          value: this.excludeId,
        },
      });
    }

    const searchBody: SearchDTO = {
      searchRequests: [
        {
          collectionName: "metrics",
          searchConditions,
        },
      ],
    };

    return this.searchService.getSomeSearchData<Search<"metrics">>(searchBody);
  };

  private searchByTerm = (searchTerm: string): IPromise<IParentOkrSelectorSearchResult[]> => {
    if (searchTerm.length < 3) {
      this.searchResults = [] as IParentOkrSelectorSearchResult[];
      this.suggestions = [] as IParentOkrSelectorSuggestionSession[];
      return this.$q.resolve([] as IParentOkrSelectorSearchResult[]);
    }

    const parseCollectionName = (collectionName: string) => {
      if (["goals", "metrics"].includes(collectionName)) {
        return collectionName.replace("s", "");
      } else {
        return "";
      }
    };

    const sessions = this.sessionIds?.filter(Boolean);
    const searchFields = [
      {
        name: "name",
      },
      {
        name: "description",
      },
      {
        name: "assignees.name",
      },
    ];

    const goalsSearchConditions: ISearchCondition[] = [];
    const metricsSearchConditions: ISearchCondition[] = [];

    if (sessions?.length > 0) {
      goalsSearchConditions.push({
        fieldName: "sessionId",
        fieldCondition: {
          operator: "in",
          value: sessions,
        },
      });

      metricsSearchConditions.push({
        fieldName: "session._id",
        fieldCondition: {
          operator: "in",
          value: sessions,
        },
      });
    }

    if (this.excludeId) {
      const excludeIdSearchCondition: ISearchCondition = {
        fieldName: "_id",
        fieldCondition: {
          operator: "isNot",
          value: this.excludeId,
        },
      };

      goalsSearchConditions.push(excludeIdSearchCondition);
      metricsSearchConditions.push(excludeIdSearchCondition);
    }

    const searchBody: SearchDTO = {
      searchTerm,
      operator: SearchOperatorsEnum.matchPrefix,
      searchRequests: [
        {
          collectionName: "goals",
          searchFields,
          searchConditions: goalsSearchConditions,
        },
        {
          collectionName: "metrics",
          searchFields,
          searchConditions: metricsSearchConditions,
        },
      ],
    };

    return this.searchService
      .getSomeSearchData<Search<"goals"> | Search<"metrics">>(searchBody)
      .then((results) => {
        this.searchResults = results.items
          .map(
            (it) =>
              Object.assign({}, it.fields, {
                id: it.id,
                type: parseCollectionName(it.collectionName),
              }) as unknown as IParentOkrSelectorSuggestion
          )
          .sort(this.sortByOwnerIdOccurrencesAndIdOrder);

        this.suggestions = [];

        return this.searchResults;
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);

        return [];
      });
  };

  private getUsersTeamsAndManagers = (ids: string[] = []): void => {
    const getParentTeamById = (teamId: string) => this.teams.find(({ id }) => teamId === id);
    const results = [];

    ids.forEach((it) => {
      const team = this.teams.find(({ id }) => id === it);

      if (team) {
        // If owner is a team, get parent team + manager
        const parentTeam = getParentTeamById(team.parentId);
        if (parentTeam) {
          results.push(parentTeam.id);
          results.push(parentTeam.manager);
        }
      } else {
        // If owner is a user, get his/her team + manager and parent team + manager
        const teams = this.teams?.filter(({ manager, members }) => it === manager || members?.some((member) => it === member));

        teams.forEach(({ id, manager, parentId }) => {
          results.push(id);
          results.push(manager);

          const parentTeam = getParentTeamById(parentId);
          if (parentTeam) {
            results.push(parentTeam.id);
            results.push(parentTeam.manager);
          }
        });
      }
    });

    this.ownerIdsToFilterBy = [...new Set(results)].filter(Boolean);
  };

  private sortByOwnerIdOccurrencesAndIdOrder = (a: IParentOkrSelectorEntry, b: IParentOkrSelectorEntry): number => {
    const orderInGivenOwnerIdsTeamsAndManagerIdsReducer = (previousValue, currentValue) => previousValue + this.ownerIdsToFilterBy.indexOf(currentValue);

    const occurrencesA = a.ownerIds.filter((it) => this.ownerIdsToFilterBy.includes(it));
    const occurrencesB = b.ownerIds.filter((it) => this.ownerIdsToFilterBy.includes(it));

    if (occurrencesA.length === occurrencesB.length) {
      const sumA = occurrencesA.reduce(orderInGivenOwnerIdsTeamsAndManagerIdsReducer, 0);
      const sumB = occurrencesB.reduce(orderInGivenOwnerIdsTeamsAndManagerIdsReducer, 0);

      return sumA - sumB;
    } else {
      return occurrencesB.length - occurrencesA.length;
    }
  };

  private afterSearch = (): void => {
    this.loadingIndicators.search = {
      progress: false,
    };
  };

  private loadParent = (parent: Partial<IParentOkrSelectorEntry>) => {
    const { id, type } = parent;
    const [selectedParent] = this.selected;

    const isParentAlreadyLoaded = selectedParent?.id === id;
    if (isParentAlreadyLoaded) {
      return;
    }

    this.loadingIndicators.selectedItem = {
      progress: true,
    };

    let parentPromise: IPromise<IGoal | IMetric>;

    if (parent.isDraft) {
      const { name } = parent;
      parentPromise = this.$q.resolve({ id, name });
    } else if (type === "goal") {
      parentPromise = this.goalService.getGoal(id);
    } else if (type === "metric") {
      parentPromise = this.metricService.getMetric(id);
    }

    if (!parentPromise) {
      this.loadingIndicators.selectedItem = {
        progress: false,
      };

      return;
    }

    parentPromise
      .then(({ id, name }) => {
        this.selected = [{ id, name, type }];
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      })
      .finally(() => {
        this.loadingIndicators.selectedItem = {
          progress: false,
        };
      });
  };
}

export const ParentOkrSelectorComponent: IComponentOptions = {
  controller: ParentOkrSelectorCtrl,
  templateUrl: require("./parent-okr-selector.jade"),
  bindings: {
    selectedOkr: "<",
    sessionIds: "<",
    ownerIds: "<",
    excludeId: "<",
    placeholder: "<",
    disabled: "<",
    borderless: "<",
    onChange: "&",
  },
};
